import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, Loader2, TrendingDown, AlertTriangle, CheckCircle, UserPlus, FileText, BarChart3, MapPin, DollarSign, History, Shield, Download, ShoppingCart, Wrench, Target, Lock, ExternalLink, Cog, Search, Crown } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScoreGauge } from "./ScoreGauge";
import { HeatMap } from "./HeatMap";
import { calcularPreco, calcularLanceSeguro, formatBRL, sendToWebhook, saveSimulation, type AnalysisResult, type UserLocation, type SimulatorData, type LanceBreakdown } from "@/lib/simulator";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { downloadPdf } from "@/lib/downloadPdf";

interface Props {
  valorFipe: number;
  valorFipeFormatado: string;
  marcaNome: string;
  modeloNome: string;
  anoNome: string;
  km: number;
  leiloeiro: string;
  observacoes: string;
  fotoUrls: string[];
  localizacao?: UserLocation;
  isPro?: boolean;
  simulatorData?: Partial<SimulatorData>;
  onNextStep?: () => void;
  onAnalysisComplete?: (result: AnalysisResult) => void;
  onBack?: () => void;
}

export function StepResultado({
  valorFipe, valorFipeFormatado, marcaNome, modeloNome, anoNome, km, leiloeiro, observacoes, fotoUrls,
  localizacao, isPro = false, simulatorData, onNextStep, onAnalysisComplete, onBack
}: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("Analisando imagens...");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const savedRef = useRef(false);
  const [savedSimId, setSavedSimId] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const msgs = ["Pesquisando preços na OLX...", "Buscando custo de peças...", "Gerando laudo com IA...", "Finalizando análise..."];
    let i = 0;
    const interval = setInterval(() => {
      i++;
      if (i < msgs.length) setLoadingMsg(msgs[i]);
    }, 4000);

    sendToWebhook({
      marca: marcaNome, modelo: modeloNome, ano: anoNome, km,
      valor_fipe: valorFipe, leiloeiro, observacoes, imagens: fotoUrls,
      modo: isPro ? "pro" : "gratuito",
      localizacao: localizacao?.cidade ? localizacao : undefined,
    })
      .then((r) => {
        setResult(r);
        onAnalysisComplete?.(r);
        // Auto-save
        if (!savedRef.current && simulatorData) {
          savedRef.current = true;
          saveSimulation(simulatorData, r, localizacao).then((id) => {
            if (id) {
              setSavedSimId(id);
              toast.success("Simulação salva automaticamente!");
            }
          });
        }
      })
      .finally(() => { setLoading(false); clearInterval(interval); });

    return () => clearInterval(interval);
  }, []);

  const NumberTicker = ({ value, duration = 2, formatter = (v: number) => v.toString() }: { value: number, duration?: number, formatter?: (v: number) => string }) => {
    const [displayValue, setDisplayValue] = useState(0);
    useEffect(() => {
      let start = 0;
      const end = value;
      if (start === end) return;

      const totalFrames = duration * 60;
      const increment = end / totalFrames;
      let currentFrame = 0;

      const timer = setInterval(() => {
        currentFrame++;
        setDisplayValue(Math.min(end, increment * currentFrame));
        if (currentFrame >= totalFrames) clearInterval(timer);
      }, 1000 / 60);

      return () => clearInterval(timer);
    }, [value, duration]);

    return <span>{formatter(displayValue)}</span>;
  };

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center gap-8 py-20 min-h-[400px]">
        <div className="relative">
          <Loader2 className="w-16 h-16 animate-spin text-primary opacity-20" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ opacity: [0.2, 1, 0.2] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              <Shield className="w-8 h-8 text-primary" />
            </motion.div>
          </div>
        </div>

        <div className="space-y-4 text-center">
          <p className="text-2xl font-heading font-black uppercase tracking-tighter animate-pulse">{loadingMsg}</p>
          <div className="flex gap-4 justify-center">
            {[Cog, Shield, Search, BarChart3].map((Icon, idx) => (
              <motion.div
                key={idx}
                animate={{
                  color: ["#666", "#FF0000", "#666"],
                  scale: [1, 1.2, 1]
                }}
                transition={{ repeat: Infinity, duration: 1, delay: idx * 0.2 }}
              >
                <Icon className="w-5 h-5" />
              </motion.div>
            ))}
          </div>
        </div>

        <div className="w-full max-w-xs bg-muted/20 h-1 overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 16, ease: "linear" }}
          />
        </div>
      </motion.div>
    );
  }

  if (!result) return null;

  const price = calcularPreco(valorFipe, result.score_condicao, km, parseInt(anoNome || "0"), localizacao?.estado);
  const nivelBadge = result.nivel_dano === "leve"
    ? { variant: "default" as const, icon: CheckCircle }
    : result.nivel_dano === "moderado"
      ? { variant: "secondary" as const, icon: AlertTriangle }
      : { variant: "destructive" as const, icon: AlertTriangle };

  // Lance Seguro: usa valor da IA se disponível, senão calcula localmente
  const custoReparo = result.custo_reparo_estimado || 0;
  const lanceCalc = calcularLanceSeguro(price.valorEstimado, custoReparo);
  const lanceFinal = (result.lance_seguro_sugerido && result.lance_seguro_sugerido > 0)
    ? result.lance_seguro_sugerido
    : lanceCalc.lance;
  const margemFinal = (result.margem_lucro_estimada && result.margem_lucro_estimada > 0)
    ? result.margem_lucro_estimada
    : lanceCalc.margem;
  const breakdown = lanceCalc.breakdown;
  const isFromAI = !!(result.lance_seguro_sugerido && result.lance_seguro_sugerido > 0);
  const reportFeatures = [
    { icon: BarChart3, text: "Análise detalhada de cada avaria identificada" },
    { icon: MapPin, text: "Mapa de calor completo com percentuais por região" },
    { icon: DollarSign, text: "Estimativa de custo de reparo por peça" },
    { icon: History, text: "Histórico FIPE dos últimos 12 meses" },
    { icon: Shield, text: "Score de risco detalhado com justificativas" },
    { icon: Download, text: "PDF profissional para download e compartilhamento" }
  ];

  // Pro users see all, free users see only ONE teaser
  const FREE_VISIBLE = isPro ? Infinity : 1;

  const isOptimistic = (margemFinal > 0.35) || (margemFinal > 0.15 && result.score_condicao > 80);
  const isModerate = (margemFinal > 0.2) || (margemFinal > 0.10 && result.score_condicao > 70);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-12"
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-4 pb-6 border-b border-border/50">
          <div className="flex items-center justify-between">
            <h2 className="text-3xl font-heading font-black tracking-tighter uppercase text-primary">Relatório IA</h2>
            {isPro && (
              <Badge className="bg-primary text-primary-foreground uppercase tracking-widest text-[10px] font-bold py-1">
                PRO ACTIVE
              </Badge>
            )}
          </div>
          <p className="text-xl text-muted-foreground font-medium uppercase tracking-wide">
            {marcaNome} {modeloNome} • {anoNome}
          </p>
        </div>

        {/* ═══════════════════════════════════════════════════
            🎯 HERO: LANCE SEGURO SUGERIDO
        ═══════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative p-8 sm:p-10 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.15)] bg-primary/5 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 -translate-y-20 translate-x-20 rotate-45 pointer-events-none"></div>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Left: Lance Value */}
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <Target className="w-7 h-7 text-primary" />
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-black uppercase tracking-[0.2em] text-primary">Lance Seguro Sugerido</h3>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-primary/60 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[250px]">
                      Valor máximo recomendado para compra para garantir lucro após reparos, taxas e margem de segurança.
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <p className="text-5xl sm:text-6xl font-heading font-black tracking-tighter text-foreground leading-none">
                <NumberTicker value={lanceFinal} formatter={formatBRL} duration={2} />
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className="border-2 border-primary bg-primary/10 font-black uppercase text-[10px] tracking-widest rounded-none px-3 py-1">
                  <Shield className="w-3 h-3 mr-2" /> Protocolo 70::30
                </Badge>
                {isFromAI && (
                  <Badge variant="secondary" className="font-bold uppercase text-[10px] tracking-widest rounded-none">
                    IA
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground font-mono">
                  Margem: <span className="text-primary font-bold">{(margemFinal * 100).toFixed(0)}%</span>
                </span>
              </div>
              <div className="p-3 bg-foreground/5 border-l-2 border-primary mt-2">
                <p className="text-[10px] text-muted-foreground leading-relaxed uppercase font-bold tracking-tight">
                  Regra de Segurança: O lance sugerido é limitado a 70% do valor real estimado para mitigar riscos de leilão e garantir margem de lucro.
                </p>
              </div>
            </div>

            {/* Right: Breakdown de Cálculo */}
            <div className="w-full lg:w-72 bg-foreground/5 p-5 space-y-3 border-l-4 border-primary/30">
              <div className="flex items-center gap-2">
                <p className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground">Breakdown de Cálculo</p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground/60 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[200px]">
                    Detalhamento dos valores subtraídos do preço de mercado.
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="space-y-2 font-mono text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor Mercado</span>
                  <span className="font-bold">{formatBRL(breakdown.valorMercado)}</span>
                </div>
                {breakdown.custoReparo > 0 && (
                  <div className="flex justify-between text-destructive">
                    <span>(−) Reparos</span>
                    <span className="font-bold">−{formatBRL(breakdown.custoReparo)}</span>
                  </div>
                )}
                <div className="flex justify-between text-destructive/70">
                  <span>(−) Margem 15%</span>
                  <span className="font-bold">−{formatBRL(breakdown.margemSeguranca)}</span>
                </div>
                <div className="border-t-2 border-foreground pt-2 flex justify-between text-primary">
                  <span className="font-black">(=) LANCE</span>
                  <span className="font-black">{formatBRL(breakdown.lanceFinal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* KM Alert / AI Verdict (Full Width) */}
          <div className="mt-8 flex flex-col gap-6">
            <div className="w-full rounded-none border-4 border-primary/20 bg-primary/5 p-8 space-y-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-3 mb-2 pb-2 border-b border-primary/10">
                <Search className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-heading font-black uppercase tracking-tighter">Veredito Técnico</h3>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed italic font-medium">"{result.analise_tecnica}"</p>
              <div className="pt-4 border-t-2 border-primary/20">
                <p className="text-base font-black uppercase text-primary tracking-tight">
                  <span className="bg-primary text-primary-foreground px-2 py-0.5 mr-2">RECOMENDAÇÃO:</span>
                  {result.recomendacao}
                </p>
              </div>
            </div>

            {price.kmAlert && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 border-4 border-destructive bg-destructive/10 text-destructive flex flex-col justify-center gap-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.05)]"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 shrink-0" />
                  <span className="font-black uppercase tracking-widest text-lg">Alerta Crítico de Quilometragem</span>
                </div>
                <p className="text-sm leading-relaxed font-bold">{price.kmAlert}</p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* 📊 SUPPORTING DATA */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="flex flex-col justify-center gap-4 bg-muted/20 p-6 rounded-none border-l-4 border-primary">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Saúde Estrutural</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  Score de 0 a 100 baseado na análise visual da IA. Avalia integridade estrutural e estética.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center justify-between">
              <div className="relative">
                <ScoreGauge score={result.score_condicao} />
                <div className="absolute -bottom-2 flex gap-1 justify-center w-full">
                  {['STR', 'MTR', 'GRN'].map((sys) => (
                    <motion.span
                      key={sys}
                      animate={{ opacity: [1, 0.4, 1] }}
                      transition={{ repeat: Infinity, duration: 2, delay: Math.random() }}
                      className="text-[8px] font-black bg-primary/20 px-1 border border-primary/30 text-primary"
                    >
                      {sys}::OK
                    </motion.span>
                  ))}
                </div>
              </div>
              <Badge variant={nivelBadge.variant} className="text-sm px-4 py-2 font-bold uppercase">
                <nivelBadge.icon className="w-4 h-4 mr-2" />
                Dano {result.nivel_dano}
              </Badge>
              {result.analise_fotos_concluida && (
                <Badge variant="outline" className="text-[10px] border-primary text-primary px-2 font-black uppercase flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Fotos Analisadas
                </Badge>
              )}
            </div>
          </div>

          <div className="flex flex-col justify-center gap-4 bg-muted/20 p-6 rounded-none border-l-4 border-accent">
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Dissonância de Mercado</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px]">
                  Comparativo entre o valor de tabela (FIPE) e o valor real estimado após análise técnica e regional.
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase">FIPE Cheia vs Valor Real Estimado</p>
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-xl font-medium line-through decoration-destructive">{valorFipeFormatado}</span>
                <span className="text-3xl font-heading font-black text-primary">
                  <NumberTicker
                    value={price.valorEstimado}
                    formatter={formatBRL}
                    duration={1.5}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Opportunity / Risk Indicator */}
        <div className="space-y-4 bg-muted/20 p-8 border-4 border-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h4 className="text-sm font-black uppercase tracking-widest text-muted-foreground">Indicador de Oportunidade</h4>
            <div className="flex items-center gap-3 justify-center md:justify-start">
              <span className={`text-2xl font-heading font-black uppercase ${isOptimistic ? 'text-primary' : isModerate ? 'text-accent' : 'text-destructive'}`}>
                {isOptimistic ? 'EXCELENTE OPORTUNIDADE' : isModerate ? 'RENTABILIDADE MÉDIA' : 'ALTO RISCO'}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={`w-8 h-8 flex items-center justify-center font-black text-xs ${
                  (margemFinal * 10) >= level ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground opacity-30'
                }`}
              >
                {level}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono bg-destructive/10 text-destructive p-3 inline-flex">
          <TrendingDown className="w-4 h-4" />
          <span>
            DEPRECIAÇÃO COMPUTADA: <NumberTicker value={price.desconto * 100} formatter={(v) => v.toFixed(1)} />%
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3.5 h-3.5 text-destructive/60 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]">
              Percentual total de desvalorização aplicado sobre a FIPE devido ao estado, KM e uso do veículo.
            </TooltipContent>
          </Tooltip>
        </div>

        <HeatMap data={result.mapa_calor} />

        {/* OLX Market Price */}
        {((result.valor_medio_mercado != null && result.valor_medio_mercado > 0) || (result.fontes_olx && result.fontes_olx.length > 0)) && (
          <div className="rounded-none border-2 border-foreground/10 bg-muted/30 p-8 space-y-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 -translate-y-16 translate-x-16 rounded-full blur-2xl"></div>
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-6 h-6 text-primary" />
                  <h3 className="text-xl font-heading font-black uppercase tracking-tighter">Pulso de Mercado (OLX)</h3>
                </div>
                <p className="text-xs text-muted-foreground uppercase font-bold tracking-widest">Base de Preços Reais para Liquidez</p>
              </div>
              <div className="bg-primary/10 border-2 border-primary/30 p-4 min-w-[200px] text-center">
                <p className="text-sm font-bold uppercase text-primary/60 mb-1">Média Detectada</p>
                <p className="text-3xl font-heading font-black text-primary">
                  {formatBRL(result.valor_medio_mercado || 0)}
                </p>
              </div>
            </div>

            {result.fontes_olx && result.fontes_olx.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 relative z-10">
                {result.fontes_olx.map((f, i) => (
                  <div 
                    key={i} 
                    className={`flex items-start justify-between p-4 bg-background border-2 transition-all group gap-4 ${f.is_restricted ? 'border-dashed border-primary/30 opacity-80' : 'border-border'}`}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black text-primary uppercase">Anúncio {i + 1}</span>
                        {f.is_restricted && <Lock className="w-2.5 h-2.5 text-primary/50" />}
                      </div>
                      <span className={`text-xs font-bold leading-tight break-words ${f.is_restricted ? 'blur-[3px] select-none' : ''}`}>
                        {f.titulo}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`font-heading font-black text-base whitespace-nowrap ${f.is_restricted ? 'blur-[4px] select-none' : ''}`}>
                        {f.is_restricted ? 'R$ 00.000' : formatBRL(f.preco)}
                      </span>
                      {f.url && !f.is_restricted && (
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 border-2 border-dashed border-border bg-background/50 text-center relative z-10">
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-widest">Nenhum anúncio direto detectado para esta configuração</p>
              </div>
            )}
            
            {!isPro && (result.fontes_olx_restricted_count ?? 0) > 0 && (
              <div className="flex items-center justify-center p-4 bg-foreground/5 relative z-10">
                <Button onClick={() => onNextStep?.()} variant="ghost" size="sm" className="font-black uppercase tracking-widest text-primary hover:bg-transparent">
                  <Crown className="w-4 h-4 mr-2" /> Desbloquear fontes extras no PRO
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Repair Costs Grid */}
        {(result.custo_reparo_estimado != null && result.custo_reparo_estimado > 0) && (
          <div className="rounded-none border-2 border-foreground/10 bg-accent/5 p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Wrench className="w-6 h-6 text-accent" />
                  <h3 className="text-xl font-heading font-black uppercase tracking-tighter">Logística de Reparos</h3>
                </div>
                <p className="text-xs text-muted-foreground uppercase font-bold tracking-widest">Mão de Obra e Peças Estimadas</p>
              </div>
              <div className="bg-destructive/10 border-2 border-destructive/30 p-4 min-w-[200px] text-center">
                <p className="text-sm font-bold uppercase text-destructive/60 mb-1">Custo Total Previsto</p>
                <p className="text-3xl font-heading font-black text-destructive">
                  {formatBRL(result.custo_reparo_estimado)}
                </p>
              </div>
            </div>

            {/* Separador GRADE DE PEÇAS */}
            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-border/20"></div>
              </div>
              <div className="relative flex justify-center text-[10px] uppercase">
                <span className="bg-accent/5 px-4 text-muted-foreground font-black tracking-[0.3em]">Grade de Peças</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.detalhes_reparo && result.detalhes_reparo.length > 0 ? (
                result.detalhes_reparo.map((p, i) => (
                  <div 
                    key={i} 
                    className={`flex items-start justify-between p-4 bg-background border-2 transition-all group gap-4 ${p.is_restricted ? 'border-dashed border-accent/30 opacity-80' : 'border-border hover:border-accent'}`}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black text-accent uppercase">Item {i + 1}</span>
                        {p.is_restricted && <Lock className="w-2.5 h-2.5 text-accent/50" />}
                      </div>
                      <span className={`text-xs font-bold leading-tight group-hover:text-accent transition-colors break-words ${p.is_restricted ? 'blur-[3px] select-none' : ''}`}>
                        {p.peca}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`font-heading font-black text-base whitespace-nowrap ${p.is_restricted ? 'blur-[4px] select-none' : ''}`}>
                        {p.is_restricted ? 'R$ 0.000' : formatBRL(p.custo)}
                      </span>
                      {p.url && !p.is_restricted && (
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-accent p-1">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 border-2 border-dashed border-border bg-background/50 text-center col-span-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase italic">Nenhuma peça física detectada para este estágio</p>
                </div>
              )}
            </div>

            {/* Separador SERVIÇOS E LOGÍSTICA */}
            <div className="relative py-4 mt-4">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-border/20"></div>
              </div>
              <div className="relative flex justify-center text-[10px] uppercase">
                <span className="bg-accent/5 px-4 text-muted-foreground font-black tracking-[0.3em]">Serviços e Logística</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.servicos_logistica && result.servicos_logistica.length > 0 ? (
                <>
                  {result.servicos_logistica.map((p, i) => (
                    <div 
                      key={`svc-${i}`} 
                      className={`flex items-start justify-between p-4 bg-background border-2 transition-all group gap-4 ${p.is_restricted ? 'border-dashed border-primary/30 opacity-80' : 'border-border hover:border-primary/40'}`}
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-black text-primary uppercase">Serviço {i + 1}</span>
                          {p.is_restricted && <Lock className="w-2.5 h-2.5 text-primary/50" />}
                        </div>
                        <span className={`text-xs font-bold leading-tight group-hover:text-primary transition-colors break-words ${p.is_restricted ? 'blur-[3px] select-none' : ''}`}>
                          {p.item}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`font-heading font-black text-base whitespace-nowrap ${p.is_restricted ? 'blur-[4px] select-none' : ''}`}>
                          {p.is_restricted ? 'R$ 0.000' : formatBRL(p.custo)}
                        </span>
                        {p.url && !p.is_restricted && (
                          <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary p-1">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Specialized Labor Card At Bottom */}
                  {result.mao_de_obra_info && (
                    <div className="flex items-center justify-between p-6 bg-primary/5 border-2 border-primary/20 col-span-1 md:col-span-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.05)]">
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-primary uppercase mb-1">Mão de Obra e Alinhamento Técnico</span>
                        <span className="text-base font-bold text-foreground leading-tight">{result.mao_de_obra_info}</span>
                      </div>
                      <Wrench className="w-6 h-6 text-primary" />
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 border-2 border-dashed border-border bg-background/50 text-center col-span-2">
                  <p className="text-xs text-muted-foreground font-medium uppercase">Incluído no custo total previsto</p>
                </div>
              )}
            </div>

            {!isPro && (result.detalhes_reparo_restricted_count ?? 0) > 0 && (
              <div className="flex items-center justify-center p-4 bg-foreground/5">
                <Button onClick={() => onNextStep?.()} variant="ghost" size="sm" className="font-black uppercase tracking-widest text-accent hover:bg-transparent">
                  <Lock className="w-4 h-4 mr-2" /> Ver plano de peças completo no PRO
                </Button>
              </div>
            )}
          </div>
        )}

      </div>


      {!isPro && (
        <div className="relative overflow-hidden border-4 border-primary bg-primary/5 p-8 sm:p-12 text-center space-y-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
          <div className="space-y-2">
            <h3 className="text-2xl font-heading font-black uppercase tracking-tighter">Acesso Restrito Detectado</h3>
            <p className="text-muted-foreground font-medium max-w-xl mx-auto">
              Você está visualizando uma prévia do laudo. Assine o plano <span className="text-primary font-bold">PRO</span> para desbloquear o histórico na nuvem, gerar PDFs profissionais e acessar todos os links de peças e anúncios reais.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md mx-auto">
            <Button onClick={() => onNextStep?.()} size="xl" className="group font-black uppercase tracking-widest bg-primary text-primary-foreground hover:scale-105 transition-transform w-full">
              <Crown className="mr-2 w-5 h-5" />
              Desbloquear Relatório Completo
            </Button>
            <Button variant="outline" size="xl" onClick={() => setShowReportPreview(true)} className="border-2 border-foreground font-black uppercase tracking-widest w-full">
              <FileText className="mr-2 w-5 h-5" />
              Ver Modelo PDF
            </Button>
          </div>
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">Garantia de lucro baseada em 5.000+ análises</p>
        </div>
      )}

      {isPro && (
        <div className="relative overflow-hidden border-4 border-primary bg-primary/5 p-8 text-center space-y-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-heading font-bold uppercase tracking-tighter">Plano Pro Ativo</h3>
          </div>
          <p className="text-sm text-muted-foreground font-medium">
            Você tem acesso completo a todos os links, peças e análises detalhadas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button
              variant="default"
              size="lg"
              className="font-black uppercase tracking-widest"
              disabled={!savedSimId || downloadingPdf}
              onClick={async () => {
                if (!savedSimId) return;
                setDownloadingPdf(true);
                await downloadPdf(savedSimId);
                setDownloadingPdf(false);
              }}
            >
              <Download className="mr-2 w-4 h-4" />
              {downloadingPdf ? "Gerando..." : "Baixar PDF do Relatório"}
            </Button>
            {savedSimId && (
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-foreground font-black uppercase tracking-widest"
                onClick={() => navigate("/userhome")}
              >
                <History className="mr-2 w-4 h-4" />
                Dossiês Salvos
              </Button>
            )}
          </div>
        </div>
      )}

      <Dialog open={showReportPreview} onOpenChange={setShowReportPreview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Exemplo de Relatório Detalhado
            </DialogTitle>
            <DialogDescription>
              Veja o que está incluso no relatório completo para assinantes.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 border p-6 space-y-4">
            <div className="flex items-center gap-3 border-b pb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-heading font-bold text-sm">Relatório AutoCarBid</p>
                <p className="text-xs text-muted-foreground">Análise Completa do Veículo</p>
              </div>
            </div>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-2.5 rounded-full bg-muted/60" style={{ width: `${100 - i * 15}%` }} />
              ))}
            </div>
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-full md:w-1/3">
                <ScoreGauge score={result.score_condicao} />
                {price.kmAlert && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mt-4 p-3 border-2 border-destructive bg-destructive/10 text-destructive text-xs font-black uppercase tracking-wider flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {price.kmAlert}
                  </motion.div>
                )}
              </div>
              <div className="w-full md:w-2/3 space-y-6">
                {[1, 2].map((i) => (
                  <div key={i} className="h-2.5 rounded-full bg-muted/60" style={{ width: `${90 - i * 20}%` }} />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold">O relatório completo inclui:</p>
            <ul className="space-y-2">
              {reportFeatures.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <Button onClick={() => { setShowReportPreview(false); navigate("/cadastro"); }} className="w-full">
            <UserPlus className="mr-2 w-4 h-4" />
            Criar Conta e Acessar Relatórios
          </Button>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
